import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import config from "@/config";
import { NodeConfig, useNodeStore } from "@/store/node";
import { useMemo, useState } from "react";
import { Button } from "../ui/button";
import { AlertCircle, RefreshCcw } from "lucide-react";
import { queryClient } from "@/lib/query-client";
import { persister } from "@/lib/query-persister";

const { network } = config;

type NodeConfigWithNetworks = NodeConfig & { networks: string[] };
const nodelyConfigs: [string[], NodeConfigWithNetworks][] =
  network !== "localnet"
    ? [
        {
          suffix: "4160.nodely.dev",
          label: ["Nodely", "Cloudflare"],
          networks: ["mainnet", "testnet", "fnet"],
        },
        {
          suffix: "algonode.xyz",
          label: ["Nodely", "Bunny"],
          networks: ["mainnet", "testnet"],
        },
        {
          suffix: "algonode.network",
          label: ["Nodely", "DNS LB"],
          networks: ["mainnet", "testnet"],
        },
      ].map(({ suffix, label, networks }) => [
        label,
        {
          networks,
          algodHost: `https://${network}-api.${suffix}`,
          algodPort: "443",
          algodToken: "",
          indexerHost: `https://${network}-idx.${suffix}`,
          indexerPort: "443",
          indexerToken: "",
        },
      ])
    : [
        [
          ["Local"],
          {
            networks: ["localnet"],
            algodHost: "http://localhost",
            algodPort: "4001",
            algodToken:
              "aaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
            indexerHost: "http://localhost",
            indexerPort: "8980",
            indexerToken:
              "aaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
          },
        ],
      ];

export function NodeSettings() {
  const [currentLabel, setCurrentLabel] = useState("");
  const [currentAlgod, setCurrentAlgod] = useState("");
  const [currentIndexer, setCurrentIndexer] = useState("");

  const {
    algodHost,
    algodPort,
    algodToken,
    indexerHost,
    indexerPort,
    indexerToken,
    set,
  } = useNodeStore();

  const activeLabel = useMemo(() => {
    const found = nodelyConfigs.find(
      ([, cfg]) =>
        cfg.algodHost === algodHost &&
        cfg.algodPort === algodPort &&
        cfg.algodToken === algodToken &&
        cfg.indexerHost === indexerHost &&
        cfg.indexerPort === indexerPort &&
        cfg.indexerToken === indexerToken
    );
    if (!currentLabel && found) {
      setCurrentLabel(found[0].join(" / "));
      setCurrentAlgod(
        found[1].algodHost + (algodPort !== "443" ? `:${algodPort}` : "")
      );
      setCurrentIndexer(
        found[1].indexerHost + (indexerPort !== "443" ? `:${indexerPort}` : "")
      );
    }
    return found ? found[0] : ["Custom"];
  }, [
    algodHost,
    algodPort,
    algodToken,
    indexerHost,
    indexerPort,
    indexerToken,
  ]);

  const changed = activeLabel.join(" / ") !== currentLabel;

  const onValueChange = (label: string) => {
    if (!label) return;
    setCurrentLabel(label);
    const found = nodelyConfigs.find(
      ([_label, _]) => _label.join(" / ") === label
    );
    if (found) {
      setCurrentAlgod(found[1].algodHost + (algodPort !== "443" ? `:${algodPort}` : ""));
      setCurrentIndexer(found[1].indexerHost + (indexerPort !== "443" ? `:${indexerPort}` : ""));
    }
  };

  const apply = async () => {
    const found = nodelyConfigs.find(
      ([lbl]) => lbl.join(" / ") === currentLabel
    );
    if (found) set(found[1]);
    queryClient.clear()
    await persister.removeClient()
    window.location.reload();
  };

  return (
    <>
      <h4>Active Node: {activeLabel.join(" / ")}</h4>

      <ToggleGroup
        type="single"
        variant="outline"
        onValueChange={onValueChange}
        value={currentLabel}
        className="gap-3 mb-5"
      >
        {nodelyConfigs
          .filter(([_, cfg]) => cfg.networks.includes(network))
          .map(([label]) => (
            <ToggleGroupItem
              key={`${label.join("-")}-node`}
              className="border rounded-md flex flex-col w-24 h-24"
              value={label.join(" / ")}
            >
              <span></span>
              <div className="text-xl">{label[0]}</div>
              <span className="mt-1 text-xs">{label[1]}</span>
            </ToggleGroupItem>
          ))}
      </ToggleGroup>
      <p
        className={
          changed ? "text-sm text-foreground" : "text-sm text-muted-foreground"
        }
      >
        Algod endpoint: {currentAlgod}
      </p>
      <p
        className={
          changed ? "text-sm text-foreground" : "text-sm text-muted-foreground"
        }
      >
        Indexer endpoint: {currentIndexer}
      </p>
      {changed && (
        <>
          <div className="hidden md:flex grow"></div>
          <div className="flex gap-3 items-center justify-between mt-4">
            <div className="text-sm flex gap-2 items-center text-destructive dark:text-orange-500">
              <AlertCircle /> Reload to apply changes
            </div>
            <Button onClick={apply}>
              <RefreshCcw /> Apply Changes
            </Button>
          </div>
        </>
      )}
    </>
  );
}
